/**
 * The words of the verifications inbox: three tabs, two filter rows, the five
 * buttons, and how a finished card says which way it went.
 *
 * The third tab is not a verification and shares the screen anyway — an agent
 * asking to act for you is a grant, not a fact — and its copy is
 * `representations.constants.ts`'s; only the tab and the outcome labels are
 * here. What a card decides is `verifications.model.ts`'s, and the reads and
 * writes are `useVerificationsController.ts`'s.
 *
 * `OUTCOME_LABELS` alone would file a rejection under "Answered", because
 * confirm and dispute both close a request as `attested`. `STANCE_LABELS`
 * takes precedence wherever a stance came back.
 */

import type {
  AffiliationKind,
  AffiliationOutcome,
  AttestationStance,
  ProvenanceMethod,
  RepresentationOutcome,
  VerificationAnswer,
  VerificationAspect,
  VerificationRequestOutcome,
} from "@ondo/shared";

/** The three things this screen answers. */
export const VERIFICATION_TABS = [
  { id: "requests", label: "Verification requests" },
  { id: "affiliations", label: "Affiliation claims" },
  /**
   * NOT A VERIFICATION, and it shares this screen anyway.
   *
   * Nothing is attested here and nothing joins a chain of title — an agent is
   * asking to act for you, which is a grant rather than a fact. What it shares
   * with the other two is the only thing this screen is organised around:
   * somebody is waiting on this reader for an answer. A fourth destination in
   * the rail for one card type would put the same question in two places and
   * make the reader check both.
   */
  { id: "representation", label: "Representation" },
  /**
   * OND-60's studio confirmation service, and it is the one tab on this screen
   * that shows BOTH SIDES of a question.
   *
   * The other three answer "what is waiting on this reader". This one answers
   * "which confirmation cases am I a party to", and the asker's own
   * commissions are in it beside the studio's queue — because a case has a
   * target, a reviewer and a fee, and both parties need to read all three. A
   * destination of its own for the studio's half would put the same case in two
   * places and make a reviewer check both.
   */
  { id: "service", label: "Studio service" },
] as const;

export type VerificationTab = (typeof VERIFICATION_TABS)[number]["id"];

/** Awaiting / resolved, and everything. */
export const STATUS_FILTERS = [
  { id: "all", label: "All" },
  { id: "awaiting", label: "Awaiting you" },
  { id: "resolved", label: "Resolved" },
] as const;

export type StatusFilter = (typeof STATUS_FILTERS)[number]["id"];

/**
 * The type chips.
 *
 * `acquisition` renders under the entry's own `method` label — "Gift", "Sale",
 * "Bequest" — because that is the fact being confirmed and the registry already
 * records it. A chip that said "Acquisition" would be the interface inventing a
 * word over one the schema has.
 */
export const ASPECT_FILTERS = [
  { id: "all", label: "All types", glyph: "" },
  { id: "entry", label: "The entry", glyph: "▥" },
  { id: "ownership", label: "Ownership", glyph: "◉" },
  { id: "document", label: "Document", glyph: "▤" },
  { id: "acquisition", label: "Acquisition", glyph: "✦" },
] as const;

export type AspectFilter = (typeof ASPECT_FILTERS)[number]["id"];

/**
 * `entry` is the default and reads under the KIND rather than under this map.
 *
 * "Entry verification" is a phrase nobody in this trade says, and the request is
 * about an exhibition or a condition report, which the registry has a word for.
 * `cardTitle` reads `HISTORY_KINDS[request.kind].chip` for this one; the string
 * here is what a filter chip and a fallback need.
 */
export const ASPECT_LABELS: Record<VerificationAspect, string> = {
  entry: "Entry verification",
  ownership: "Ownership verification",
  acquisition: "Acquisition verification",
  document: "Document verification",
};

export const ASPECT_BLURBS: Record<VerificationAspect, string> = {
  entry: "Confirm what this entry says",
  ownership: "Confirm who held this work, and when",
  acquisition: "Confirm how the work changed hands",
  document: "Confirm a document is genuine and as described",
};

/** How the work changed hands, as a chip on an acquisition request. */
export const METHOD_LABELS: Record<ProvenanceMethod, string> = {
  created_by_artist: "Made by the artist",
  purchase: "Purchase",
  commission: "Commission",
  gift: "Gift",
  bequest: "Bequest",
  inheritance: "Inheritance",
  exchange: "Exchange",
  auction: "Auction",
  consignment: "Consignment",
  restitution: "Restitution",
  unknown: "Unknown",
};

/**
 * The five buttons.
 *
 * `refer` is not an answer — it opens a request for somebody else — so it sits
 * apart from the four that end the question, and the model keeps it out of
 * `answers` for the same reason.
 */
export const ANSWER_LABELS: Record<VerificationAnswer, string> = {
  confirm: "✓ Yes / Confirm",
  dispute: "✕ No / Dispute",
  no_recall: "? Don’t recall",
  decline: "Not my place to say",
};

export const ACTION_LABELS = {
  addDocument: "✎ Add document",
  refer: "↪ Refer to someone",
  bulk: "☑ Bulk actions",
  view: "View ›",
} as const;

/** What each answer asks for before it will send. */
export const ANSWER_COMPOSERS: Record<
  VerificationAnswer,
  { label: string; placeholder: string; required: boolean }
> = {
  confirm: {
    label: "Anything to add (optional)",
    placeholder: "Our records show the sale in November 1962.",
    required: false,
  },
  dispute: {
    label: "Why you dispute this",
    placeholder: "The Zurich collection sold before 1962.",
    required: true,
  },
  no_recall: {
    label: "Anything to add (optional)",
    placeholder: "Nobody here goes back that far.",
    required: false,
  },
  decline: {
    label: "Anything to add (optional)",
    placeholder: "Not something we comment on.",
    required: false,
  },
};

export const REFER_COMPOSER = {
  label: "ONDO id of the account better placed to answer",
  placeholder: "ONDO-1042",
  /**
   * The same sentence the ask composer gives, and for the same reason. Repeated
   * rather than shared because a reader here needs it here — being told twice
   * why there is no search costs nothing, and being told nowhere costs a support
   * ticket.
   */
  help: "There is no name search: ONDO will not tell one account whether another exists, so an id is the only way to name a person here.",
  /**
   * The note field beside the id, which had neither a label nor a name.
   *
   * It was the only control on these four screens a screen reader announced as
   * an unlabelled text box, and its placeholder was a literal in the view while
   * every other string on the screen came from here.
   */
  noteLabel: "Why them (optional)",
  notePlaceholder: "Why them, in a sentence.",
  action: "Pass it on",
} as const;

/**
 * Outcome chips, on the resolved side.
 *
 * `attested` is the fallback and not the ordinary case. Both confirming and
 * disputing end a request as `attested` — the stance is what tells them apart —
 * so a card that read its chip from here alone would file a rejection under
 * "Answered" and lose the only part of it the reader cares about. See
 * `STANCE_LABELS`, which takes precedence wherever a stance came back.
 */
export const OUTCOME_LABELS: Record<VerificationRequestOutcome, string> = {
  attested: "Answered",
  declined: "Declined",
  no_recall: "Didn’t recall",
  referred: "Passed on",
  withdrawn: "Withdrawn",
  expired: "Expired",
};

/** What an `attested` request actually said. */
export const STANCE_LABELS: Record<AttestationStance, string> = {
  verify: "Confirmed",
  dispute: "Disputed",
};

/**
 * The sentence a resolved card leads its record with.
 *
 * Keyed by stance where there was one and by outcome otherwise, which is why
 * `verify` and `dispute` sit beside `attested` rather than under it. Each names
 * the ACT and what it did to the record, because "resolved" is not an answer:
 * the reader already knows they replied, and what they want back is which way.
 *
 * All of them are written without a subject on purpose. A request addressed to
 * an organisation may have been answered by any editor of it, and "you
 * disputed this" would hand one colleague another's act; `withdrawn` and
 * `expired` were nobody at this end's doing at all.
 */
export const RESOLUTION_LEADS: Record<
  VerificationRequestOutcome | AttestationStance,
  string
> = {
  verify: "Confirmed — the answer stands beside the link on the record.",
  dispute: "Disputed — the objection stands beside the link, with the reason given.",
  attested: "Answered. The stance recorded with it is not shown here.",
  declined: "Declined — the request is closed and nothing was recorded for or against the claim.",
  no_recall: "Answered “don’t recall” — closed with no position taken, which is not a no.",
  referred: "Passed on to somebody better placed. The question is open with them, not with you.",
  withdrawn: "The asker withdrew this before it was answered.",
  expired: "This closed without an answer.",
};

/** The record of what was answered, on a card that has been. */
export const RESOLUTION_COPY = {
  title: "WHAT WAS RECORDED",
  /** A dispute must say why; the other three may. Two labels, not one. */
  reasonLabel: "REASON GIVEN",
  noteLabel: "WHAT WAS ADDED",
  /**
   * Said out loud rather than left as a gap. An answer with nothing beside it is
   * a real thing to have recorded, and an empty space reads as a missing fetch.
   */
  silent: "Nothing was added with it.",
} as const;

export const AFFILIATION_KIND_LABELS: Record<AffiliationKind, string> = {
  employment: "Employment",
  representation: "Representation",
  directorship: "Directorship",
  advisory: "Advisory",
  membership: "Membership",
  other: "Affiliation",
};

export const AFFILIATION_OUTCOME_LABELS: Record<AffiliationOutcome, string> = {
  confirmed: "Confirmed",
  disputed: "Disputed",
  declined: "Declined",
  no_recall: "Didn’t recall",
  withdrawn: "Withdrawn",
  expired: "Expired",
};

/**
 * How a finished representation request reads on the card.
 *
 * Three, where an affiliation claim has six. There is no "no recall" and no
 * "declined to engage": being asked to appoint an agent is a proposal about the
 * future, and the only honest answers to a proposal are yes and no — plus the
 * asker taking it back.
 */
export const REPRESENTATION_OUTCOME_LABELS: Record<RepresentationOutcome, string> = {
  granted: "Granted",
  declined: "Declined",
  withdrawn: "Withdrawn by the agent",
};

/**
 * How many requests one bulk action may cover.
 *
 * A cap for the reason `MAX_ACTS_PER_ENTRY` is one. Confirming is putting a name
 * behind somebody else's claim, and a control that did forty at a time would be
 * a control nobody read forty claims before using.
 */
export const MAX_BULK = 25;

export const VERIFICATION_COPY = {
  loading: "Loading what has been asked of you…",
  subtitle: "Confirm or dispute ONDO information others have asked you to verify",
  awaitingBanner:
    "Other ONDO members ask you to confirm something you are positioned to know — a link in a chain of title, how a work changed hands, or a document. Answer yes, no, or “don’t recall”, add a supporting document, or pass it to somebody better placed.",
  /**
   * Said plainly, because the alternative is a user who thinks silence is an
   * answer. It is the same sentence `PROVENANCE_STATE_COPY.askedNote` gives the
   * other side.
   */
  noObligation: "Nobody is obliged to answer, and nothing is concluded from silence.",
  awaitingBadge: "AWAITING YOUR RESPONSE",
  requestedBy: "REQUESTED BY",
  askedOf: "ASKED OF",
  workInQuestion: "WORK IN QUESTION",
  whatToVerify: "WHAT YOU’RE BEING ASKED TO VERIFY",
  datesAndContext: "DATES & CONTEXT BEING VERIFIED",
  documentsToReview: "DOCUMENTS TO REVIEW",
  /** The accessible name of the control that opens one. See `DocumentRow`. */
  openDocument: "Open",
  withheld: "Withheld",
  /** Rendered where a role chip would go. The registry records no role here. */
  capacityHint: "Asked of you as",
  documentAwaiting: "AWAITING CONFIRMATION",
  documentConfirmed: "✓ CONFIRMED",
  documentNeither: "not yet confirmed on ONDO",
  onBehalfLabel: "Answer as",
  onBehalfSelf: "Yourself",
  /** Only shown when there is something to choose. See `capacitiesFor`. */
  onBehalfHelp:
    "Whose name goes on the answer. Only organisations you are an editor of, because committing an institution’s word is not a junior job.",
  positionHeld:
    "You already hold a position on this link. Take the one you have back first.",
  withdrawn: "This request was withdrawn while you were looking at it.",
  alreadyAnswered: "This was already answered somewhere else.",
  /**
   * A link that outlived what it pointed at.
   *
   * Three real causes and one sentence for all of them, deliberately: answered
   * from another seat at the same organisation, withdrawn by the asker, or no
   * longer visible to this account. Naming which would mean telling a reader
   * something the inbox itself would not — and "it is not here" is the part
   * they can act on.
   */
  focusMissing:
    "That request is no longer here. It may have been answered from another seat, withdrawn by whoever asked, or closed. Everything still open is below.",
  bulkConfirm: "You are about to put your name to each of these:",
  bulkReasonLabel: "One reason, applied to all of them",
} as const;

export const EMPTY_COPY = {
  never: {
    lead: "Nothing has been asked of you.",
    detail:
      "When another ONDO member asks you to confirm something you are positioned to know — a link in a chain of title, how a work changed hands, a document — it will appear here.",
  },
  awaiting: {
    lead: "Nothing is waiting on you.",
    detail: "Everything asked of you has been answered. The record is below.",
  },
  filtered: {
    lead: "Nothing matches those filters.",
    detail: "Widen them to see the rest.",
  },
  representation: {
    lead: "No agent has asked to represent one of your works.",
    detail:
      "An agent asks by entering a work’s ONDO code, which is on the work’s record and reaches them only if you give it to them. Granting lets them read the record, correct the catalogue entry and write the work’s history — and nothing else: they cannot sell it, lend it, re-share it, or appoint anybody else.",
  },
  /**
   * OND-60. Said from both sides, because this tab is read from both: a
   * collector who has asked nobody and a studio nobody has asked see the same
   * sentence, and it has to be true of each.
   */
  service: {
    lead: "No studio confirmation cases.",
    detail:
      "A case appears here when somebody asks an authorised studio to confirm that a work is by its artist — whether you asked or you were asked. The studio's answer is a paid service with a target date; the target is what it aims at and never a promise that an answer will come.",
  },
  affiliations: {
    lead: "No affiliation claims.",
    detail:
      "When somebody says they work, or worked, with you or with an organisation you are an editor of, you will be asked to confirm it here. Confirming records the fact and grants nothing — it opens no workspace and hands over no access.",
  },
} as const;
